// src/components/TopicMafia/components/FinalResultDialog.jsx
import React, { useMemo } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fade,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { mainActionBtnSx } from "../topicMafiaUi";

function getScoreForPlayer(finalScores, p) {
    if (!finalScores) return null;

    const userId = p?.user_id != null ? String(p.user_id) : null;
    const playerId = p?.id != null ? String(p.id) : null;

    if (userId && finalScores[userId] != null) return finalScores[userId];
    if (playerId && finalScores[playerId] != null) return finalScores[playerId];

    // 숫자 키 케이스 대비
    if (p?.user_id != null && finalScores[p.user_id] != null) return finalScores[p.user_id];
    if (p?.id != null && finalScores[p.id] != null) return finalScores[p.id];

    return null;
}

export default function FinalResultDialog({
    open,
    onGoLobby,
    onGoWaiting,
    room,
    players = [],
    playerNameMap,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const finalScores = room?.final_scores || null;
    const victory = room?.victory || null; // "MAFIA" / "CIVIL" / etc
    const reason = room?.final_score_reason || null;

    const mafiaGuessCorrect = room?.mafia_guess_correct;
    const mafiaGuessText = room?.mafia_guess_text ?? "";
    const guessRound = room?.mafia_guess_round ?? null;

    // ✅ topics (ENDED/GUESS에서 state에 포함되어 있어야 함)
    const topicA = room?.topic_a ?? null;
    const topicB = room?.topic_b ?? null;
    const st = room?.phase || room?.status;
    const showTopics = st === "ENDED" || st === "GUESS";

    // ✅ 마피아(추측자) 공개: final_loser_player_id
    const finalLoserPlayerId = room?.final_loser_player_id ?? null;
    const finalLoserIsMafia = room?.final_loser_is_mafia === true;
    // ✅ 최종 꼴찌가 “마피아일 때만” 마피아로 표시
    const mafiaPlayerId = finalLoserIsMafia ? finalLoserPlayerId : null;

    const mafiaName = useMemo(() => {
        if (!mafiaPlayerId) return null;
        const p = (players || []).find((x) => Number(x?.id) === Number(mafiaPlayerId));
        return (
            p?.nickname ||
            p?.username ||
            playerNameMap?.get?.(Number(mafiaPlayerId)) ||
            `#${mafiaPlayerId}`
        );
    }, [mafiaPlayerId, players, playerNameMap]);

    const rows = useMemo(() => {
        const list = (players || []).map((p) => {
            const name =
                p?.nickname ||
                p?.username ||
                playerNameMap?.get?.(Number(p?.id)) ||
                `#${p?.id}`;

            const score = getScoreForPlayer(finalScores, p);
            const scoreNum = typeof score === "number" ? score : -999999;

            return {
                key: `${p?.id}`,
                playerId: p?.id,
                userId: p?.user_id,
                name,
                score: score == null ? "-" : score,
                scoreNum,
                alive: !!p?.is_alive,
            };
        });

        return list.sort((a, b) => b.scoreNum - a.scoreNum);
    }, [players, playerNameMap, finalScores]);

    const victoryLabel = useMemo(() => {
        if (!victory) return "결과";
        if (victory === "MAFIA") return "마피아 승";
        if (victory === "CIVIL") return "시민 승";
        return String(victory);
    }, [victory]);

    const victorySeverity =
        victory === "MAFIA" ? "error" : victory === "CIVIL" ? "success" : "info";

    // ✅ 문구 연출 강화
    const guessLine = useMemo(() => {
        if (typeof mafiaGuessCorrect !== "boolean") return null;
        if (mafiaGuessCorrect) return "🧠 마피아의 추측이 적중했습니다!";
        return "❌ 마피아의 추측이 빗나갔습니다.";
    }, [mafiaGuessCorrect]);

    return (
        <Dialog
            open={open}
            onClose={() => { }}
            disableEscapeKeyDown
            fullScreen={isMobile}
            fullWidth
            maxWidth={isMobile ? false : "sm"}
            TransitionComponent={Fade}
            transitionDuration={180}
        >
            <DialogTitle sx={{ fontWeight: 900 }}>✅ 게임 종료 - 최종 결과</DialogTitle>

            <DialogContent dividers sx={isMobile ? { pb: 2 } : undefined}>
                <Stack spacing={2.2}>
                    {/* ✅ 승패/추측 결과 */}
                    <Alert severity={victorySeverity} sx={{ fontWeight: 800 }}>
                        <b>{victoryLabel}</b>

                        {/* (2) 마피아 정체 공개 */}
                        {mafiaName && (
                            <>
                                <br />
                                🕵️ 마피아: <b>{mafiaName}</b>
                            </>
                        )}

                        {/* (4) 라운드 정보 */}
                        {guessRound != null && (
                            <>
                                <br />
                                마지막 추측 라운드: <b>{guessRound}R</b>
                            </>
                        )}

                        {/* (1) 문구 강화 */}
                        {guessLine && (
                            <>
                                <br />
                                {guessLine}{" "}
                                {typeof mafiaGuessCorrect === "boolean" && (
                                    <b>{mafiaGuessCorrect ? " (정답 +3)" : ""}</b>
                                )}
                            </>
                        )}

                        {reason && (
                            <>
                                <br />
                                {reason}
                            </>
                        )}
                    </Alert>

                    {/* ✅ 주제 공개 영역 */}
                    {showTopics && (topicA || topicB || mafiaGuessText) && (
                        <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 3 }}>
                            <Stack spacing={1.2}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <Typography sx={{ fontWeight: 900 }}>주제 공개</Typography>
                                    {topicA && (
                                        <Chip size="small" color="success" label="시민" sx={{ fontWeight: 900 }} />
                                    )}
                                    {topicB && (
                                        <Chip size="small" color="error" label="마피아" sx={{ fontWeight: 900 }} />
                                    )}
                                </Stack>

                                <Stack spacing={1}>
                                    {topicA && (
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 900, color: "success.main" }}>
                                                시민 주제
                                            </Typography>
                                            <Typography sx={{ fontWeight: 900 }}>{topicA}</Typography>
                                        </Box>
                                    )}

                                    {topicB && (
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 900, color: "error.main" }}>
                                                마피아 주제
                                            </Typography>
                                            <Typography sx={{ fontWeight: 900 }}>{topicB}</Typography>
                                        </Box>
                                    )}

                                    {mafiaGuessText && (
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 900, color: "text.secondary" }}>
                                                마피아가 입력한 추측
                                            </Typography>
                                            <Typography sx={{ fontWeight: 900 }}>{mafiaGuessText}</Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </Stack>
                        </Paper>
                    )}

                    {/* ✅ 점수표 */}
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <Typography sx={{ fontWeight: 900 }}>유저별 점수</Typography>
                            <Chip size="small" variant="outlined" label={`${rows.length}명`} />
                        </Stack>

                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
                            <Stack spacing={1}>
                                {rows.map((r, idx) => (
                                    <Box key={r.key}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                                <Chip size="small" label={`#${idx + 1}`} variant="outlined" />
                                                <Typography
                                                    sx={{
                                                        fontWeight: 900,
                                                        minWidth: 0,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {r.name}
                                                </Typography>
                                                {!r.alive && <Chip size="small" color="default" label="탈락" />}
                                            </Stack>

                                            <Chip
                                                label={`${r.score}점`}
                                                color={typeof r.score === "number" && r.score > 0 ? "primary" : "default"}
                                                sx={{ fontWeight: 900 }}
                                            />
                                        </Stack>
                                        {idx !== rows.length - 1 && <Divider sx={{ my: 1 }} />}
                                    </Box>
                                ))}

                                {!finalScores && (
                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                        ⚠️ final_scores가 아직 내려오지 않았어. 백엔드 state에 final_scores 포함되는지 확인해줘.
                                    </Alert>
                                )}
                            </Stack>
                        </Paper>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions
                sx={
                    isMobile
                        ? { position: "sticky", bottom: 0, bgcolor: "background.paper", zIndex: 1 }
                        : undefined
                }
            >
                <Button
                    onClick={onGoWaiting}
                    variant="contained"
                    sx={mainActionBtnSx}
                    fullWidth={isMobile}
                >
                    이 방에서 다시하기
                </Button>

                <Button
                    onClick={onGoLobby}
                    variant="outlined"
                    sx={mainActionBtnSx}
                    fullWidth={isMobile}
                >
                    로비로
                </Button>
            </DialogActions>
        </Dialog>
    );
}
